import { useEffect, useState, useCallback } from "react";

const DISMISS_KEY = "aeoibcms-pwa-install-dismissed-at";
const DISMISS_DAYS = 7;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iPadOS 13+ reports as Mac, so check touch capability too
  return /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && "ontouchend" in document);
}

function dismissedRecently() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!at) return false;
    const ageMs = Date.now() - at;
    return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * useInstallPrompt — exposes:
 *  - canInstall: boolean (true on Android/desktop Chrome when beforeinstallprompt fired)
 *  - showIOSHint: boolean (true on iOS Safari since it can't auto-install)
 *  - alreadyInstalled: boolean (running as standalone PWA)
 *  - dismissed: boolean (user dismissed within DISMISS_DAYS)
 *  - promptInstall(): triggers the native install dialog (Android/desktop only)
 *  - dismiss(): hides the banner for DISMISS_DAYS
 */
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(() => dismissedRecently());

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onStandaloneChange = (e) => e.matches && setInstalled(true);

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    mq?.addEventListener?.("change", onStandaloneChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
      mq?.removeEventListener?.("change", onStandaloneChange);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: "unavailable" };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice;
  }, [deferred]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  return {
    canInstall: !!deferred && !installed,
    showIOSHint: isIOS() && !installed && !dismissed,
    alreadyInstalled: installed,
    dismissed,
    promptInstall,
    dismiss,
  };
}
